import React, { useEffect, useMemo, useRef, useState } from "react";

export type HorizontalDragDropSide = "left" | "right";

export interface HorizontalDragDropItem {
  id: number;
  text: string;
  answer: HorizontalDragDropSide;
}

export interface HorizontalDragDropState {
  leftIds: number[];
  middleIds: number[];
  rightIds: number[];
  correctColumnById: Record<number, HorizontalDragDropSide>;
}

interface HorizontalDragDropProps {
  items: HorizontalDragDropItem[];
  leftTitle?: React.ReactNode;
  rightTitle?: React.ReactNode;
  initialSelectedIds?: number[];
  leftColor?: string;
  rightColor?: string;
  disabled?: boolean;
  onProgressChange?: (state: HorizontalDragDropState, isComplete: boolean) => void;
  onComplete?: (state: HorizontalDragDropState) => void;
}

const CORRECT_LINE = "rgb(31,122,80)";
const CORRECT_SOFT = "rgba(237,247,241,0.94)";
const WRONG_LINE = "rgb(196,42,32)";
const NEUTRAL_BORDER = "rgba(162,164,170,0.55)";
const SUBTEXT = "Drag toward a side, or tap below";
const WRONG_TEXT = "Not quite - try the other side.";
const DEFAULT_LEFT_COLOR = "#737B99";
const DEFAULT_RIGHT_COLOR = "#FE8165";
const EMPTY_SELECTED_IDS: number[] = [];
const FOCUS_VISIBLE_CLASS = "focus-visible:outline focus-visible:outline-[3px] focus-visible:outline-offset-[3px] focus-visible:outline-foundation-orange-500";

const prefersReducedMotion = () =>
  typeof window.matchMedia === "function"
  && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

let stylesInjected = false;

const useInjectedStyles = () => {
  useEffect(() => {
    if (stylesInjected) return;
    stylesInjected = true;

    const el = document.createElement("style");
    el.setAttribute("data-hdd", "");
    el.textContent = `
      .hdd { font-family: Inter, system-ui, sans-serif; color: var(--fg, #353947); }
      .hdd-instr { font: 400 14px/1.5 Inter, system-ui, sans-serif; color: var(--fg-muted, #5d616c); margin: 0 0 8px; }
      .hdd-progress { font: 500 12px/1 Inter, system-ui, sans-serif; color: var(--fg-subtle, #787a84); text-align: center; margin: 6px 0 14px; }
      .hdd-arena { display: flex; gap: 16px; align-items: stretch; margin-top: 8px; }
      .hdd-center { flex: 1 1 auto; display: flex; flex-direction: column; align-items: center; justify-content: center; min-height: 190px; }
      .hdd-target { flex: 0 0 200px; border-radius: 18px; border: 1.5px dashed; font: 600 15px Inter, system-ui, sans-serif; cursor: pointer; display: flex; align-items: center; justify-content: center; gap: 8px; padding: 18px 12px; background: transparent; transition: background .15s ease, filter .15s ease; }
      .hdd-target:hover:not(:disabled) { filter: brightness(.98); }
      .hdd-target:disabled { cursor: not-allowed; opacity: .68; }
      .hdd-cardwrap { position: relative; width: 100%; min-height: 150px; display: flex; justify-content: center; }
      .hdd-ghost { position: absolute; border-radius: 18px; background: rgba(255,255,255,.5); border: 1px solid rgba(162,164,170,.3); }
      .hdd-card { position: relative; z-index: 2; width: 82%; max-width: 420px; background: #fff; border: 1.5px solid ${NEUTRAL_BORDER}; border-radius: 18px; padding: 34px 20px; box-shadow: 0 10px 24px -8px rgba(15,18,30,.22); cursor: grab; touch-action: none; user-select: none; text-align: center; }
      .hdd-card:active { cursor: grabbing; }
      .hdd-card.is-disabled { cursor: not-allowed; opacity: .68; }
      .hdd-card-label { font-family: "Neue Montreal", system-ui, sans-serif; font-weight: 400; font-size: 22px; line-height: 27px; color: var(--fg-heading, #262832); overflow-wrap: anywhere; }
      .hdd-card-sub { font: 400 12.5px Inter, system-ui, sans-serif; color: var(--fg-subtle, #787a84); margin-top: 9px; }
      .hdd-card-sub.is-wrong { color: ${WRONG_LINE}; }
      .hdd-back { display: block; margin: 14px auto 0; background: none; border: 0; color: var(--fg-muted, #5d616c); font: 500 13px Inter, system-ui, sans-serif; cursor: pointer; text-decoration: underline; }
      .hdd-back:disabled { cursor: not-allowed; opacity: .68; }
      .hdd-pill { padding: 10px 18px; border-radius: 50px; background: transparent; border: 1px solid var(--foundation-base-darkbase-dark-500, #353947); color: var(--foundation-base-darkbase-dark-500, #353947); font: 600 13.5px Inter, system-ui, sans-serif; cursor: pointer; }
      .hdd-pill:disabled { cursor: not-allowed; opacity: .68; }
      .hdd-done { position: relative; }
      .hdd-done-header { display: flex; align-items: flex-start; justify-content: space-between; gap: 16px; margin: 6px 0 14px; }
      .hdd-done-title { font-family: "Neue Montreal", system-ui, sans-serif; font-weight: 400; font-size: 22px; line-height: 27px; letter-spacing: -.02em; color: var(--fg-heading, #262832); margin: 0 0 2px; }
      .hdd-done-sub { font: 400 13px/1.4 Inter, system-ui, sans-serif; color: var(--fg-muted, #5d616c); }
      .hdd-pill-secondary { flex: none; padding: 9px 18px; border-radius: 50px; background: transparent; border: 1px solid var(--foundation-base-darkbase-dark-200, #a2a4aa); color: var(--fg-muted, #5d616c); font: 600 13px Inter, system-ui, sans-serif; cursor: pointer; white-space: nowrap; }
      .hdd-pill-secondary:disabled { cursor: not-allowed; opacity: .68; }
      .hdd-groups { display: flex; gap: 16px; }
      .hdd-group { flex: 1; border-radius: 18px; padding: 16px; }
      .hdd-group-label { font: 600 12px/1 Inter, system-ui, sans-serif; text-transform: uppercase; letter-spacing: .05em; margin-bottom: 10px; }
      .hdd-chip { display: flex; align-items: center; padding: 12px 14px; border-radius: 12px; font: 500 14px Inter, system-ui, sans-serif; margin-bottom: 8px; overflow-wrap: anywhere; }
      @keyframes hdd-check { 0% { opacity: 0; transform: scale(.5); } 60% { opacity: 1; transform: scale(1.14); } 100% { opacity: 1; transform: scale(1); } }
      @media (max-width: 560px) {
        .hdd-arena { flex-wrap: wrap; }
        .hdd-center { order: -1; flex: 1 1 100%; }
        .hdd-target { flex: 1 1 44%; padding: 13px; font-size: 14px; }
        .hdd-groups { flex-direction: column; }
      }
    `;

    document.head.appendChild(el);
  }, []);
};

export const HorizontalDragDrop: React.FC<HorizontalDragDropProps> = ({
  items,
  leftTitle = "Left",
  rightTitle = "Right",
  initialSelectedIds = EMPTY_SELECTED_IDS,
  leftColor = DEFAULT_LEFT_COLOR,
  rightColor = DEFAULT_RIGHT_COLOR,
  disabled = false,
  onProgressChange,
  onComplete,
}) => {
  useInjectedStyles();

  const correctColumnById = useMemo(
    () =>
      items.reduce<Record<number, HorizontalDragDropSide>>((acc, item) => {
        acc[item.id] = item.answer;
        return acc;
      }, {}),
    [items],
  );
  const itemById = useMemo(() => new Map(items.map((item) => [item.id, item])), [items]);
  const initialPlacementOrder = useMemo(
    () =>
      initialSelectedIds.filter((id) => {
        const item = itemById.get(id);
        return Boolean(item && correctColumnById[id] === item.answer);
      }),
    [correctColumnById, initialSelectedIds, itemById],
  );

  const [placementOrder, setPlacementOrder] = useState<number[]>(initialPlacementOrder);
  const [wrongMessage, setWrongMessage] = useState(false);
  const completedRef = useRef(false);
  const cardRef = useRef<HTMLDivElement | null>(null);
  const subRef = useRef<HTMLDivElement | null>(null);
  const leftRef = useRef<HTMLButtonElement | null>(null);
  const rightRef = useRef<HTMLButtonElement | null>(null);
  const busyRef = useRef(false);
  const wrongTimerRef = useRef<number | null>(null);
  const animationTimerRef = useRef<number | null>(null);
  const cleanupTimerRef = useRef<number | null>(null);

  useEffect(() => {
    setPlacementOrder(initialPlacementOrder);
    completedRef.current = false;
    busyRef.current = false;
  }, [initialPlacementOrder]);

  useEffect(() => {
    return () => {
      if (wrongTimerRef.current) window.clearTimeout(wrongTimerRef.current);
      if (animationTimerRef.current) window.clearTimeout(animationTimerRef.current);
      if (cleanupTimerRef.current) window.clearTimeout(cleanupTimerRef.current);
    };
  }, []);

  const placedIdSet = useMemo(() => new Set(placementOrder), [placementOrder]);
  const current = items.find((item) => !placedIdSet.has(item.id)) ?? null;
  const done = items.length > 0 && placementOrder.length >= items.length;
  const horizontalState = useMemo<HorizontalDragDropState>(() => {
    const leftIds = placementOrder.filter((id) => correctColumnById[id] === "left");
    const rightIds = placementOrder.filter((id) => correctColumnById[id] === "right");
    const middleIds = items.filter((item) => !placedIdSet.has(item.id)).map((item) => item.id);

    return {
      leftIds,
      middleIds,
      rightIds,
      correctColumnById,
    };
  }, [correctColumnById, items, placedIdSet, placementOrder]);

  useEffect(() => {
    onProgressChange?.(horizontalState, done);

    if (done && !completedRef.current) {
      completedRef.current = true;
      onComplete?.(horizontalState);
    }

    if (!done) {
      completedRef.current = false;
    }
  }, [done, horizontalState, onComplete, onProgressChange]);

  const resetCardStyles = () => {
    const card = cardRef.current;
    if (!card) return;

    card.style.transition = "none";
    card.style.transform = "";
    card.style.opacity = "";
    card.style.borderColor = NEUTRAL_BORDER;
    card.style.outline = "";
    const check = card.querySelector("[data-hdd-check]");
    if (check) check.remove();
  };

  const commit = (side: HorizontalDragDropSide) => {
    if (!current || side !== current.answer) return;
    setPlacementOrder((previous) => (previous.includes(current.id) ? previous : [...previous, current.id]));
    busyRef.current = false;
  };

  const pulseTarget = (side: HorizontalDragDropSide) => {
    const el = side === "left" ? leftRef.current : rightRef.current;
    if (!el?.animate) return;

    el.animate(
      [
        { boxShadow: "0 0 0 0 rgba(31,122,80,0.6)", transform: "scale(1)" },
        { boxShadow: "0 0 0 16px rgba(31,122,80,0)", transform: "scale(1.04)" },
        { boxShadow: "0 0 0 0 rgba(31,122,80,0)", transform: "scale(1)" },
      ],
      { duration: 760, easing: "ease-out" }
    );
  };

  const affirmCorrect = (card: HTMLDivElement, dir: number, cb: () => void) => {
    card.style.transition = "transform .24s ease, border-color .2s ease";
    card.style.borderColor = CORRECT_LINE;
    card.style.outline = "4px solid rgba(31,122,80,0.45)";

    const overlay = document.createElement("div");
    overlay.setAttribute("data-hdd-check", "");
    overlay.textContent = "\u2713";
    overlay.style.cssText =
      "position:absolute;inset:0;display:flex;align-items:center;justify-content:center;" +
      `font:700 66px Inter,system-ui,sans-serif;color:${CORRECT_LINE};background:${CORRECT_SOFT};` +
      "border-radius:inherit;z-index:4;animation:hdd-check .44s cubic-bezier(.2,.85,.3,1.2) both;";
    card.appendChild(overlay);

    card.animate?.(
      [{ transform: "scale(1)" }, { transform: "scale(1.06)" }, { transform: "scale(1)" }],
      { duration: 440, easing: "ease" }
    );

    animationTimerRef.current = window.setTimeout(() => {
      card.style.transition = "transform .36s ease, opacity .36s ease";
      card.style.transform = `translateX(${dir * 540}px) rotate(${dir * 16}deg)`;
      card.style.opacity = "0";

      cleanupTimerRef.current = window.setTimeout(() => {
        resetCardStyles();
        cb();
      }, 380);
    }, 620);
  };

  const shakeWrong = (card: HTMLDivElement, reduceMotion: boolean) => {
    card.style.transition = reduceMotion ? "none" : "transform .18s ease, border-color .15s ease";
    card.style.transform = "";
    card.style.borderColor = WRONG_LINE;
    card.style.outline = "3px solid rgba(196,42,32,0.4)";
    setWrongMessage(true);

    if (!reduceMotion) {
      card.animate?.(
        [
          { transform: "translateX(0)" }, { transform: "translateX(-9px)" },
          { transform: "translateX(9px)" }, { transform: "translateX(-6px)" },
          { transform: "translateX(6px)" }, { transform: "translateX(0)" },
        ],
        { duration: 380, easing: "ease" }
      );
    }

    if (wrongTimerRef.current) window.clearTimeout(wrongTimerRef.current);
    wrongTimerRef.current = window.setTimeout(() => {
      card.style.borderColor = NEUTRAL_BORDER;
      card.style.outline = "";
      setWrongMessage(false);
    }, 1100);
  };

  const resolve = (side: HorizontalDragDropSide) => {
    if (busyRef.current || disabled || done || !current) return;

    const card = cardRef.current;
    const reduceMotion = prefersReducedMotion();
    if (side !== current.answer) {
      if (card) shakeWrong(card, reduceMotion);
      return;
    }

    busyRef.current = true;
    setWrongMessage(false);
    if (wrongTimerRef.current) window.clearTimeout(wrongTimerRef.current);

    if (reduceMotion) {
      resetCardStyles();
      commit(side);
      return;
    }

    pulseTarget(side);
    const dir = side === "left" ? -1 : 1;

    if (card) {
      affirmCorrect(card, dir, () => commit(side));
      return;
    }

    commit(side);
  };

  const stopSwipePropagation = (event: React.TouchEvent<HTMLDivElement>) => {
    event.stopPropagation();
  };

  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    e.stopPropagation();

    if (busyRef.current || disabled || done) return;

    const card = cardRef.current;
    if (!card) return;

    const reduceMotion = prefersReducedMotion();
    const drag = { startX: e.clientX, dx: 0 };
    card.style.transition = "none";

    try {
      card.setPointerCapture(e.pointerId);
    } catch (err) {
      // Pointer capture can fail in test environments.
    }

    const move = (ev: PointerEvent) => {
      drag.dx = ev.clientX - drag.startX;
      card.style.transform = reduceMotion
        ? `translateX(${drag.dx}px)`
        : `translateX(${drag.dx}px) rotate(${drag.dx * 0.05}deg)`;
      card.style.borderColor =
        drag.dx < -40 ? leftColor : drag.dx > 40 ? rightColor : NEUTRAL_BORDER;
    };

    const up = (ev: PointerEvent) => {
      window.removeEventListener("pointermove", move);

      try {
        card.releasePointerCapture(ev.pointerId);
      } catch (err) {
        // Pointer capture can fail in test environments.
      }

      const threshold = 70;
      if (drag.dx <= -threshold) {
        card.style.transform = "";
        resolve("left");
      } else if (drag.dx >= threshold) {
        card.style.transform = "";
        resolve("right");
      } else {
        card.style.transition = reduceMotion ? "none" : "transform .2s ease";
        card.style.transform = "";
        card.style.borderColor = NEUTRAL_BORDER;
      }
    };

    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up, { once: true });
  };

  const goBack = () => {
    if (disabled || busyRef.current || placementOrder.length === 0) return;
    resetCardStyles();
    setPlacementOrder((previous) => previous.slice(0, -1));
  };

  const reset = () => {
    if (disabled || busyRef.current) return;
    resetCardStyles();
    setPlacementOrder([]);
  };

  const grouped = (side: HorizontalDragDropSide) => items.filter((item) => item.answer === side);

  return (
    <div className="hdd" data-testid="horizontal-drag-drop" data-swipe-ignore="true">
      {!done ? (
        <>
          <p className="hdd-instr">
            Sort each card. It only moves on once it&apos;s placed correctly.
          </p>
          <div className="hdd-progress">{`Card ${placementOrder.length + 1} of ${items.length}`}</div>
          <div className="hdd-arena">
            <button
              ref={leftRef}
              type="button"
              className={`hdd-target ${FOCUS_VISIBLE_CLASS}`}
              style={{ borderColor: alpha(leftColor, 0.55), background: alpha(leftColor, 0.12), color: darken(leftColor) }}
              onClick={() => resolve("left")}
              disabled={disabled}
            >
              {"\u2190 "}{leftTitle}
            </button>

            <div className="hdd-center">
              <div className="hdd-cardwrap">
                <div className="hdd-ghost" style={{ top: 9, width: "74%", height: "86%", transform: "rotate(-2.5deg)" }} />
                <div className="hdd-ghost" style={{ top: 4, width: "78%", height: "92%", transform: "rotate(2deg)" }} />
                {current ? (
                  <div
                    ref={cardRef}
                    className={`hdd-card${disabled ? " is-disabled" : ""}`}
                    onPointerDown={handlePointerDown}
                    onTouchStart={stopSwipePropagation}
                    onTouchMove={stopSwipePropagation}
                    onTouchEnd={stopSwipePropagation}
                    data-testid="horizontal-drag-card"
                    data-swipe-ignore="true"
                  >
                    <div className="hdd-card-label">{current.text}</div>
                    <div ref={subRef} className={`hdd-card-sub${wrongMessage ? " is-wrong" : ""}`}>
                      {wrongMessage ? WRONG_TEXT : SUBTEXT}
                    </div>
                  </div>
                ) : null}
              </div>
              {placementOrder.length > 0 ? (
                <button type="button" className={`hdd-back ${FOCUS_VISIBLE_CLASS}`} onClick={goBack} disabled={disabled}>
                  Back to previous
                </button>
              ) : null}
            </div>

            <button
              ref={rightRef}
              type="button"
              className={`hdd-target ${FOCUS_VISIBLE_CLASS}`}
              style={{ borderColor: alpha(rightColor, 0.55), background: alpha(rightColor, 0.12), color: darken(rightColor) }}
              onClick={() => resolve("right")}
              disabled={disabled}
            >
              {rightTitle}{" \u2192"}
            </button>
          </div>
        </>
      ) : (
        <div className="hdd-done">
          <div className="hdd-done-header" data-testid="horizontal-drag-complete-header">
            <div>
              <div className="hdd-done-title">Each one in its place.</div>
              <div className="hdd-done-sub">Here they are, grouped by category.</div>
            </div>
            <button type="button" className={`hdd-pill-secondary ${FOCUS_VISIBLE_CLASS}`} onClick={reset} disabled={disabled}>Start over</button>
          </div>
          <div className="hdd-groups">
            {[
              { key: "left" as const, label: leftTitle, color: leftColor },
              { key: "right" as const, label: rightTitle, color: rightColor },
            ].map((cat) => (
              <div
                key={cat.key}
                className="hdd-group"
                style={{ background: alpha(cat.color, 0.06), border: `1px solid ${alpha(cat.color, 0.25)}` }}
              >
                <div className="hdd-group-label" style={{ color: darken(cat.color) }}>{cat.label}</div>
                {grouped(cat.key).map((item) => (
                  <div
                    key={item.id}
                    className="hdd-chip"
                    style={{ background: alpha(cat.color, 0.14), border: `1px solid ${alpha(cat.color, 0.4)}`, color: darken(cat.color) }}
                  >
                    {item.text}
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const toRGB = (color: string): [number, number, number] => {
  if (color.startsWith("rgb")) {
    const parts = color
      .replace(/rgba?\(([^)]+)\)/, "$1")
      .split(",")
      .slice(0, 3)
      .map((value) => Number.parseFloat(value.trim()));

    return [parts[0] || 0, parts[1] || 0, parts[2] || 0];
  }

  let hex = color.replace("#", "").trim();
  if (hex.length === 3) {
    hex = hex.split("").map((value) => value + value).join("");
  }

  if (hex.length !== 6) {
    return [115, 123, 153];
  }

  return [
    Number.parseInt(hex.slice(0, 2), 16),
    Number.parseInt(hex.slice(2, 4), 16),
    Number.parseInt(hex.slice(4, 6), 16),
  ];
};

const alpha = (color: string, opacity: number) => {
  const [r, g, b] = toRGB(color);
  return `rgba(${r},${g},${b},${opacity})`;
};

const darken = (color: string) => {
  const [r, g, b] = toRGB(color);
  return `rgb(${Math.round(r * 0.6)},${Math.round(g * 0.6)},${Math.round(b * 0.6)})`;
};
